import { Component, OnInit, OnDestroy } from '@angular/core';
import { CnAiService, AiSidebarState } from '../challenges_service/cn-ai.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

declare var toastr: any;

@Component({
  selector: 'app-cn-ai-sidebar',
  templateUrl: './cn-ai-sidebar.component.html',
  styleUrls: ['./cn-ai-sidebar.component.scss']
})
export class CnAiSidebarComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  isOpen: boolean = false;
  stage: string = '';
  subStep: string = '';
  promptText: string = '';
  suggestions: string[] = [];
  selected: boolean[] = [];
  loading: boolean = false;
  error: string = '';

  constructor(public aiService: CnAiService) {}

  ngOnInit() {
    this.aiService.sidebar$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((state: AiSidebarState) => {
      this.isOpen = state.open;
      this.stage = state.stage;
      this.subStep = state.subStep;
      if (state.open) {
        this.promptText = this.buildDefaultPrompt(state);
      }
    });

    this.aiService.suggestions$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(suggestions => {
      this.suggestions = suggestions;
      this.selected = suggestions.map(() => false);
    });

    this.aiService.loading$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(loading => {
      this.loading = loading;
    });

    this.aiService.error$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(error => {
      this.error = error;
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildDefaultPrompt(state: AiSidebarState): string {
    if (state.promptTemplate) {
      let prompt = state.promptTemplate;
      const config = this.aiService.config$.value;
      let persona = '';
      switch (config.persona) {
        case 'first_person': persona = 'from a first-person perspective'; break;
        case 'second_person': persona = 'from a second-person perspective'; break;
        case 'third_person': persona = 'from a third-person perspective'; break;
      }
      prompt = prompt.replace('{persona}', persona);

      if (state.context) {
        let contextStr = '';
        if (typeof state.context === 'string') {
          contextStr = state.context;
        } else if (Array.isArray(state.context)) {
          contextStr = state.context
            .map((c: any) => {
              if (!c || c === '') return '';
              if (typeof c === 'string') return c;
              if (typeof c === 'object') return c.todo || c.name || c.text || c.content || JSON.stringify(c);
              return String(c);
            })
            .filter((c: string) => c !== '')
            .join(', ');
        }
        prompt = prompt.replace('{context}', contextStr);
      }
      return prompt;
    }
    return `Generate 5 suggestions for the ${state.subStep} step in the ${state.stage} stage.`;
  }

  generate() {
    if (!this.promptText.trim()) {
      this.error = 'Please enter a prompt before generating.';
      return;
    }
    this.aiService.generateSuggestions(this.promptText);
  }

  addSelected() {
    const hasSelected = this.selected.some(s => s);
    if (!hasSelected) {
      toastr.options = { closeButton: true, progressBar: true, timeOut: 2000 };
      toastr.warning('Please select at least one suggestion.', 'No Selection');
      return;
    }

    this.aiService.addSelectedToComponent(this.selected);

    toastr.options = { closeButton: true, progressBar: true, timeOut: 2000 };
    toastr.success('Suggestions added successfully!', 'Added!');

    this.close();
  }

  get allSelected(): boolean {
    return this.selected.length > 0 && this.selected.every(s => s);
  }

  selectAll() {
    const all = this.allSelected;
    this.selected = this.selected.map(() => !all);
  }

  close() {
    this.aiService.closeSidebar();
  }

  getStageLabel(): string {
    const labels: any = {
      'clarify': 'Clarify',
      'ideate': 'Ideate',
      'develop': 'Develop',
      'implement': 'Implement'
    };
    return labels[this.stage] || this.stage;
  }

  getSubStepLabel(): string {
    const labels: any = {
      'goal': 'Goal',
      'data': 'Data',
      'challenge': 'Challenge',
      'ideas': 'Ideas',
      'solution': 'Solution',
      'solution-prototype': 'Solution Prototype',
      'pluses': 'Pluses',
      'opportunities': 'Opportunities',
      'issues': 'Issues',
      'new-thinking': 'New Thinking',
      'action-plan': 'Action Plan',
      'next24': 'Next 24 Hours',
    };
    return labels[this.subStep] || this.subStep;
  }
}
